import React from "react";
import { Space } from "antd";
import { AxButton, AxCard, AxIconWrapper, AxDivider } from "@dynata/axiom3";

class Card extends React.Component {
  render() {
    return (
      <div className="section" id="card">
        <h3>Card</h3>
        <p>
          The Card element could be used to render detailed information about an
          entity and could optionally have an action button in the header to
          trigger a particular action.
        </p>

        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`<AxCard style={{ width: 300 }}>
  <p>Card content</p>
  <p>Card content</p>
  <p>Card content</p>
</AxCard>`}</pre>

          <div className="prop-header">
            <p className="props-title">Props</p>
            <p>
              For AntD props, visit{" "}
              <AxButton
                size="small"
                type="link"
                target="_blank"
                href="https://ant.design/components/card/#API"
              >
                https://ant.design/components/card/#API
              </AxButton>
            </p>
          </div>

          <table className="props-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Type</th>
                <th>Default</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>size</td>
                <td>default | small</td>
                <td>Size of the card </td>
                <td></td>
              </tr>
              <tr>
                <td>title</td>
                <td>string</td>
                <td>Card title</td>
                <td></td>
              </tr>
              <tr>
                <td>extra</td>
                <td>ReactNode</td>
                <td>Content to render in the top-right corner of the card</td>
                <td></td>
              </tr>
              <tr>
                <td>radius</td>
                <td>small | medium | large | xlarge</td>
                <td>Card's radius</td>
                <td></td>
              </tr>
              <tr>
                <td>shadow</td>
                <td>none | small | medium | large</td>
                <td>Card's shadow</td>
                <td></td>
              </tr>
              <tr>
                <td>actions</td>
                <td>The action list, shows at the bottom of the Card</td>
                <td>Array&lt;ReactNode&gt;</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>activeTabKey</td>
                <td>Current TabPane's key</td>
                <td>string</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>bodyStyle</td>
                <td>Inline style to apply to the card content</td>
                <td>CSSProperties</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>cover</td>
                <td>Card cover</td>
                <td>ReactNode</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>defaultActiveTabKey</td>
                <td>
                  Initial active TabPane's key, if <code>activeTabKey</code> is
                  not set
                </td>
                <td>string</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>extra</td>
                <td>Content to render in the top-right corner of the card</td>
                <td>ReactNode</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>headStyle</td>
                <td>Inline style to apply to the card head</td>
                <td>CSSProperties</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>loading</td>
                <td>
                  Shows a loading indicator while the contents of the card are
                  being fetched
                </td>
                <td>boolean</td>
                <td>false</td>
                <td></td>
              </tr>
              <tr>
                <td>tabBarExtraContent</td>
                <td>Extra content in tab bar</td>
                <td>ReactNode</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>tabList</td>
                <td>List of TabPane's head</td>
                <td>
                  Array<pre>{"<{key: string, tab: ReactNode}>"}</pre>
                </td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>tabProps</td>
                <td>
                  <a href="/components/tabs/#Tabs">Tabs</a>
                </td>
                <td>-</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>onTabChange</td>
                <td>Callback when tab is switched</td>
                <td>(key) =&gt; void</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>...others</td>
                <td>
                  All extra props are passed down to the{" "}
                  <>
                    <AxButton
                      size="small"
                      type="link"
                      target="_blank"
                      href="https://4x.ant.design/components/card/#API"
                    >
                      {`<Card />`}
                    </AxButton>
                  </>{" "}
                  component.
                </td>
                <td></td>
                <td>-</td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <AxDivider orientation="left" plain>
            Default
          </AxDivider>

          <Space size="large" direction="vertical">
            <pre>{`
<AxCard
title="Default"
style={{ width: 300 }}
radius="medium"
shadow="medium"
>
<p>
  Default card with <br />
  radius="medium" and shadow="medium"
</p>
</AxCard>            
            `}</pre>
            <Space align="start" size="large">
              <AxCard
                style={{ width: 300 }}
                size="small"
                radius="small"
                shadow="none"
                title="Small"
              >
                <p>
                  Small card with <br />
                  radius="small" and shadown="small"
                </p>
              </AxCard>

              <AxCard
                title="Default"
                style={{ width: 300 }}
                radius="medium"
                shadow="medium"
              >
                <p>
                  Default card with <br />
                  radius="medium" and shadow="medium"
                </p>
              </AxCard>

              <AxCard
                title="List Items"
                style={{ width: 300 }}
                radius="large"
                shadow="large"
              >
                <div>
                  <AxButton
                    type="link"
                    color="main"
                    block
                    style={{ justifyContent: "flex-start" }}
                  >
                    <AxIconWrapper type="regular" size="14" icon={"fa-star"} />{" "}
                    List Item
                  </AxButton>
                  <AxButton
                    type="link"
                    color="main"
                    block
                    style={{ justifyContent: "flex-start" }}
                  >
                    <AxIconWrapper type="regular" size="14" icon={"fa-star"} />{" "}
                    List Item
                  </AxButton>
                  <AxButton
                    type="link"
                    color="main"
                    block
                    style={{ justifyContent: "flex-start" }}
                  >
                    <AxIconWrapper type="regular" size="14" icon={"fa-star"} />{" "}
                    List Item
                  </AxButton>
                  <AxButton
                    type="link"
                    color="main"
                    block
                    style={{ justifyContent: "flex-start" }}
                  >
                    <AxIconWrapper type="regular" size="14" icon={"fa-star"} />{" "}
                    List Item
                  </AxButton>
                  <AxButton
                    type="link"
                    color="main"
                    block
                    style={{ justifyContent: "flex-start" }}
                  >
                    <AxIconWrapper type="regular" size="14" icon={"fa-star"} />{" "}
                    List Item
                  </AxButton>
                </div>
                <div style={{ float: "right" }}>
                  <AxButton type="link" color="main">
                    View All
                  </AxButton>
                </div>
              </AxCard>
            </Space>
          </Space>
        </Space>
      </div>
    );
  }
}
export default Card;
